const EVIDENCE_STATUSES = new Set([
  'verified-formula',
  'published-average',
  'published-regional-pricing',
  'example-proposal',
  'user-assumption',
]);

function fail(path, message) {
  throw new Error(
    `Invalid fundraising pricing registry at ${path}: ${message}`,
  );
}

function record(value, path) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    fail(path, 'expected an object');
  }
  return value;
}

function array(value, path) {
  if (!Array.isArray(value)) fail(path, 'expected an array');
  return value;
}

function string(value, path) {
  if (typeof value !== 'string' || value.trim() === '') {
    fail(path, 'expected a non-empty string');
  }
  return value;
}

function integer(value, path, maximum) {
  if (!Number.isSafeInteger(value) || value < 0) {
    fail(path, 'expected a non-negative safe integer');
  }
  if (maximum !== undefined && value > maximum) {
    fail(path, `must be no greater than ${maximum}`);
  }
  return value;
}

function isoDate(value, path) {
  const date = string(value, path);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    fail(path, 'expected YYYY-MM-DD');
  }
  return date;
}

function uniqueIds(items, path) {
  const ids = new Set();
  items.forEach((item, index) => {
    const id = string(
      record(item, `${path}[${index}]`).id,
      `${path}[${index}].id`,
    );
    if (ids.has(id)) fail(`${path}[${index}].id`, `duplicate id "${id}"`);
    ids.add(id);
  });
  return ids;
}

function validateConditions(conditions, path, optionChoices) {
  if (conditions === undefined) return;
  array(conditions, path).forEach((rawCondition, index) => {
    const condition = record(rawCondition, `${path}[${index}]`);
    const groupId = string(
      condition.optionGroupId,
      `${path}[${index}].optionGroupId`,
    );
    const choiceId = string(condition.choiceId, `${path}[${index}].choiceId`);
    if (!optionChoices.get(groupId)?.has(choiceId)) {
      fail(
        `${path}[${index}]`,
        `references unknown option ${groupId}:${choiceId}`,
      );
    }
  });
}

function validateFunding(rawFunding, path, feeGroups) {
  const funding = record(rawFunding, path);
  const kind = string(funding.kind, `${path}.kind`);
  if (kind === 'organization-absorbs' || kind === 'optional-tip-to-platform') {
    if (kind === 'optional-tip-to-platform') {
      if (funding.estimated !== false)
        fail(`${path}.estimated`, 'must be false in V1');
      string(funding.note, `${path}.note`);
    }
    return;
  }

  if (kind === 'donor-pays-gross-up' || kind === 'optional-fee-coverage') {
    const ids = array(funding.feeGroupIds, `${path}.feeGroupIds`);
    if (ids.length === 0) fail(`${path}.feeGroupIds`, 'must not be empty');
    ids.forEach((rawId, index) => {
      const id = string(rawId, `${path}.feeGroupIds[${index}]`);
      const group = feeGroups.get(id);
      if (!group)
        fail(`${path}.feeGroupIds[${index}]`, `unknown fee group "${id}"`);
      if (group.scope !== 'transaction') {
        fail(
          `${path}.feeGroupIds[${index}]`,
          'gross-up can reference transaction fees only',
        );
      }
    });
    if (kind === 'optional-fee-coverage') {
      if (funding.participationBasisPoints === undefined) {
        if (funding.estimated !== false) {
          fail(
            `${path}.participationBasisPoints`,
            'is required unless optional coverage is explicitly unestimated',
          );
        }
      } else {
        integer(
          funding.participationBasisPoints,
          `${path}.participationBasisPoints`,
          10000,
        );
      }
      string(funding.assumptionLabel, `${path}.assumptionLabel`);
      if (funding.estimated !== undefined && funding.estimated !== false) {
        fail(`${path}.estimated`, 'must be false when provided');
      }
    }
    return;
  }

  if (kind === 'optional-contribution-to-organization') {
    const contributionKind = string(
      funding.contributionKind,
      `${path}.contributionKind`,
    );
    if (contributionKind === 'fixed') {
      integer(funding.amountCents, `${path}.amountCents`);
    } else if (contributionKind === 'percentage') {
      integer(funding.rateBasisPoints, `${path}.rateBasisPoints`, 10000);
    } else {
      fail(`${path}.contributionKind`, 'expected fixed or percentage');
    }
    integer(
      funding.participationBasisPoints,
      `${path}.participationBasisPoints`,
      10000,
    );
    string(funding.assumptionLabel, `${path}.assumptionLabel`);
    return;
  }

  if (kind === 'donor-service-charge-to-organization') {
    integer(funding.rateBasisPoints, `${path}.rateBasisPoints`, 10000);
    return;
  }

  fail(`${path}.kind`, `unsupported funding behavior "${kind}"`);
}

export function validateFundraisingPricingRegistry(value) {
  const root = record(value, '$');
  integer(root.schemaVersion, '$.schemaVersion');
  if (root.schemaVersion !== 4) {
    fail('$.schemaVersion', 'unsupported schema version');
  }
  if (typeof root.developmentMode !== 'boolean')
    fail('$.developmentMode', 'expected a boolean');

  const categories = array(root.platformCategories, '$.platformCategories');
  if (categories.length === 0) {
    fail('$.platformCategories', 'must not be empty');
  }
  const categoryIds = uniqueIds(categories, '$.platformCategories');
  categories.forEach((rawCategory, index) => {
    const category = record(rawCategory, `$.platformCategories[${index}]`);
    string(category.label, `$.platformCategories[${index}].label`);
    string(category.legendLabel, `$.platformCategories[${index}].legendLabel`);
    string(category.iconClass, `$.platformCategories[${index}].iconClass`);
  });

  const definitions = record(root.evidenceDefinitions, '$.evidenceDefinitions');
  EVIDENCE_STATUSES.forEach((status) => {
    const definition = record(
      definitions[status],
      `$.evidenceDefinitions.${status}`,
    );
    string(definition.label, `$.evidenceDefinitions.${status}.label`);
    string(
      definition.description,
      `$.evidenceDefinitions.${status}.description`,
    );
  });

  const sources = array(root.sources, '$.sources');
  const sourceIds = uniqueIds(sources, '$.sources');
  const publicSourceIds = new Set();
  sources.forEach((rawSource, index) => {
    const source = record(rawSource, `$.sources[${index}]`);
    string(source.label, `$.sources[${index}].label`);
    isoDate(source.verifiedOn, `$.sources[${index}].verifiedOn`);
    string(source.coverageNotes, `$.sources[${index}].coverageNotes`);
    if (source.kind === 'public-url') {
      const url = string(source.url, `$.sources[${index}].url`);
      if (!/^https:\/\//.test(url))
        fail(`$.sources[${index}].url`, 'must use HTTPS');
      publicSourceIds.add(source.id);
    } else if (source.kind === 'owner-supplied-document') {
      isoDate(source.documentDate, `$.sources[${index}].documentDate`);
    } else {
      fail(`$.sources[${index}].kind`, 'unsupported source kind');
    }
  });

  const platforms = array(root.platforms, '$.platforms');
  const platformIds = uniqueIds(platforms, '$.platforms');
  const canonicalOrder = array(
    root.canonicalPlatformOrder,
    '$.canonicalPlatformOrder',
  );
  if (new Set(canonicalOrder).size !== canonicalOrder.length) {
    fail('$.canonicalPlatformOrder', 'contains duplicate platform ids');
  }
  if (
    canonicalOrder.length !== platformIds.size ||
    canonicalOrder.some((id) => !platformIds.has(id))
  ) {
    fail(
      '$.canonicalPlatformOrder',
      'must contain every platform exactly once',
    );
  }
  const defaults = array(
    root.defaultComparisonPlatformIds,
    '$.defaultComparisonPlatformIds',
  );
  if (defaults.length === 0 || defaults.some((id) => !platformIds.has(id))) {
    fail('$.defaultComparisonPlatformIds', 'must contain known platforms');
  }
  const enabledPlatformIds = new Set(
    platforms
      .filter((platform) => platform.comparisonEnabled === true)
      .map((platform) => platform.id),
  );
  if (defaults.some((id) => !enabledPlatformIds.has(id))) {
    fail('$.defaultComparisonPlatformIds', 'must contain enabled platforms');
  }
  if (!enabledPlatformIds.has('big-nest')) {
    fail('$.platforms', 'Big Nest must remain comparison enabled');
  }

  const detailPagePaths = new Set();

  platforms.forEach((rawPlatform, platformIndex) => {
    const path = `$.platforms[${platformIndex}]`;
    const platform = record(rawPlatform, path);
    string(platform.displayName, `${path}.displayName`);
    const categoryId = string(platform.categoryId, `${path}.categoryId`);
    if (!categoryIds.has(categoryId)) {
      fail(`${path}.categoryId`, `references unknown category "${categoryId}"`);
    }
    if (typeof platform.comparisonEnabled !== 'boolean') {
      fail(`${path}.comparisonEnabled`, 'expected a boolean');
    }
    if (platform.detailPagePath !== undefined) {
      const detailPagePath = string(
        platform.detailPagePath,
        `${path}.detailPagePath`,
      );
      if (
        !/^\/[A-Za-z0-9][A-Za-z0-9\-\/]*\/$/.test(detailPagePath) ||
        detailPagePath.includes('//')
      ) {
        fail(
          `${path}.detailPagePath`,
          'must be a same-site absolute path with leading and trailing slashes',
        );
      }
      if (detailPagePaths.has(detailPagePath)) {
        fail(
          `${path}.detailPagePath`,
          `duplicate detail page path "${detailPagePath}"`,
        );
      }
      detailPagePaths.add(detailPagePath);
    }
    string(platform.regionLabel, `${path}.regionLabel`);
    array(platform.qualifications, `${path}.qualifications`).forEach(
      (item, index) => string(item, `${path}.qualifications[${index}]`),
    );
    array(platform.disclosures, `${path}.disclosures`).forEach((item, index) =>
      string(item, `${path}.disclosures[${index}]`),
    );

    const evidence = record(platform.evidence, `${path}.evidence`);
    const status = string(evidence.status, `${path}.evidence.status`);
    if (!EVIDENCE_STATUSES.has(status))
      fail(`${path}.evidence.status`, 'unsupported evidence status');
    string(evidence.label, `${path}.evidence.label`);
    string(evidence.description, `${path}.evidence.description`);
    if (platform.sourceIds !== undefined) {
      array(platform.sourceIds, `${path}.sourceIds`).forEach(
        (sourceId, index) => {
          if (!sourceIds.has(sourceId)) {
            fail(`${path}.sourceIds[${index}]`, `unknown source "${sourceId}"`);
          }
        },
      );
    }
    const evidenceSources = array(
      evidence.sourceIds,
      `${path}.evidence.sourceIds`,
    );
    evidenceSources.forEach((sourceId, index) => {
      if (!sourceIds.has(sourceId))
        fail(
          `${path}.evidence.sourceIds[${index}]`,
          `unknown source "${sourceId}"`,
        );
    });
    if (status === 'verified-formula') {
      isoDate(evidence.verifiedOn, `${path}.evidence.verifiedOn`);
      if (
        evidenceSources.length === 0 ||
        !evidenceSources.some((id) => publicSourceIds.has(id))
      ) {
        fail(
          `${path}.evidence.sourceIds`,
          'verified pricing requires a public source',
        );
      }
    }

    const groups = array(platform.feeGroups, `${path}.feeGroups`);
    uniqueIds(groups, `${path}.feeGroups`);
    const feeGroups = new Map();
    groups.forEach((rawGroup, groupIndex) => {
      const groupPath = `${path}.feeGroups[${groupIndex}]`;
      const group = record(rawGroup, groupPath);
      feeGroups.set(group.id, group);
      string(group.label, `${groupPath}.label`);
      if (
        !['processing', 'platform', 'service', 'revenue-share'].includes(
          group.category,
        )
      )
        fail(`${groupPath}.category`, 'unsupported fee category');
      if (!['organization', 'donor'].includes(group.paidBy))
        fail(`${groupPath}.paidBy`, 'unsupported payer');
      if (!['processor', 'platform'].includes(group.recipient))
        fail(`${groupPath}.recipient`, 'unsupported recipient');
      if (!['transaction', 'fundraiser', 'annual'].includes(group.scope))
        fail(`${groupPath}.scope`, 'unsupported scope');
      const rules = array(group.rules, `${groupPath}.rules`);
      if (rules.length === 0) fail(`${groupPath}.rules`, 'must not be empty');
      rules.forEach((rawRule, ruleIndex) => {
        const rulePath = `${groupPath}.rules[${ruleIndex}]`;
        const rule = record(rawRule, rulePath);
        if (rule.kind === 'percentage') {
          integer(rule.rateBasisPoints, `${rulePath}.rateBasisPoints`, 10000);
          if (!['intended-gift', 'donor-charge'].includes(rule.appliedTo))
            fail(`${rulePath}.appliedTo`, 'unsupported percentage base');
        } else if (rule.kind === 'tiered-percentage-by-total') {
          if (group.scope !== 'fundraiser') {
            fail(rulePath, 'tiered total percentages require fundraiser scope');
          }
          if (rule.comparisonAmount !== 'intended-gift-total') {
            fail(
              `${rulePath}.comparisonAmount`,
              'unsupported comparison amount',
            );
          }
          if (rule.appliedTo !== 'intended-gift') {
            fail(`${rulePath}.appliedTo`, 'unsupported percentage base');
          }
          const tiers = array(rule.tiers, `${rulePath}.tiers`);
          if (tiers.length === 0)
            fail(`${rulePath}.tiers`, 'must not be empty');
          tiers.forEach((rawTier, tierIndex) => {
            const tierPath = `${rulePath}.tiers[${tierIndex}]`;
            const tier = record(rawTier, tierPath);
            integer(tier.minAmountCents, `${tierPath}.minAmountCents`);
            integer(tier.rateBasisPoints, `${tierPath}.rateBasisPoints`, 10000);
            if (tier.maxAmountCents !== undefined) {
              integer(tier.maxAmountCents, `${tierPath}.maxAmountCents`);
              if (tier.maxAmountCents < tier.minAmountCents) {
                fail(tierPath, 'maximum must not be less than minimum');
              }
            }
            if (tierIndex === 0 && tier.minAmountCents !== 0) {
              fail(tierPath, 'the first tier must start at zero');
            }
            if (tierIndex > 0) {
              const previous = tiers[tierIndex - 1];
              if (
                previous.maxAmountCents === undefined ||
                tier.minAmountCents !== previous.maxAmountCents + 1
              ) {
                fail(tierPath, 'tiers must be contiguous and non-overlapping');
              }
            }
            if (
              tierIndex < tiers.length - 1 &&
              tier.maxAmountCents === undefined
            ) {
              fail(tierPath, 'only the final tier may omit a maximum');
            }
          });
          if (tiers.at(-1).maxAmountCents !== undefined) {
            fail(`${rulePath}.tiers`, 'the final tier must be open ended');
          }
        } else if (rule.kind === 'percentage-from-option') {
          if (group.scope !== 'fundraiser') {
            fail(rulePath, 'option percentages require fundraiser scope');
          }
          string(rule.optionGroupId, `${rulePath}.optionGroupId`);
          if (
            !['fee-rate', 'organization-retained-rate'].includes(
              rule.valueMeaning,
            )
          ) {
            fail(`${rulePath}.valueMeaning`, 'unsupported percentage meaning');
          }
          if (rule.appliedTo !== 'intended-gift') {
            fail(`${rulePath}.appliedTo`, 'unsupported percentage base');
          }
        } else if (rule.kind === 'per-transaction') {
          integer(rule.amountCents, `${rulePath}.amountCents`);
          if (group.scope !== 'transaction')
            fail(rulePath, 'per-transaction rules require transaction scope');
        } else if (rule.kind === 'fixed') {
          integer(rule.amountCents, `${rulePath}.amountCents`);
          if (group.scope === 'transaction')
            fail(rulePath, 'fixed rules cannot use transaction scope');
        } else {
          fail(`${rulePath}.kind`, 'unsupported fee rule');
        }
        if (group.scope === 'annual' && rule.kind !== 'fixed')
          fail(rulePath, 'annual fees must be fixed');
      });
      if (group.maximumCents !== undefined)
        integer(group.maximumCents, `${groupPath}.maximumCents`);
    });

    const optionGroups = array(platform.optionGroups, `${path}.optionGroups`);
    uniqueIds(optionGroups, `${path}.optionGroups`);
    const optionChoices = new Map();
    optionGroups.forEach((rawGroup, groupIndex) => {
      const groupPath = `${path}.optionGroups[${groupIndex}]`;
      const group = record(rawGroup, groupPath);
      string(group.legend, `${groupPath}.legend`);
      if (group.summaryPrefix !== undefined) {
        string(group.summaryPrefix, `${groupPath}.summaryPrefix`);
      }
      if (group.input !== undefined && group.choices !== undefined) {
        fail(groupPath, 'must define choices or an input, not both');
      }
      if (group.input !== undefined) {
        const input = record(group.input, `${groupPath}.input`);
        if (input.kind !== 'percentage') {
          fail(`${groupPath}.input.kind`, 'unsupported option input');
        }
        string(input.label, `${groupPath}.input.label`);
        if (input.description !== undefined) {
          string(input.description, `${groupPath}.input.description`);
        }
        const minimum = integer(
          input.minimumBasisPoints,
          `${groupPath}.input.minimumBasisPoints`,
          10000,
        );
        const maximum = integer(
          input.maximumBasisPoints,
          `${groupPath}.input.maximumBasisPoints`,
          10000,
        );
        const step = integer(
          input.stepBasisPoints,
          `${groupPath}.input.stepBasisPoints`,
          10000,
        );
        if (maximum < minimum) {
          fail(`${groupPath}.input`, 'maximum must not be less than minimum');
        }
        if (step === 0)
          fail(`${groupPath}.input.stepBasisPoints`, 'must be positive');
        const defaultValue = Number(group.defaultChoiceId);
        if (
          !Number.isInteger(defaultValue) ||
          defaultValue < minimum ||
          defaultValue > maximum ||
          (defaultValue - minimum) % step !== 0
        ) {
          fail(`${groupPath}.defaultChoiceId`, 'must be a valid input value');
        }
        optionChoices.set(group.id, new Set([group.defaultChoiceId]));
      } else {
        const choices = array(group.choices, `${groupPath}.choices`);
        if (choices.length === 0)
          fail(`${groupPath}.choices`, 'must not be empty');
        const choiceIds = uniqueIds(choices, `${groupPath}.choices`);
        optionChoices.set(group.id, choiceIds);
        if (!choiceIds.has(group.defaultChoiceId))
          fail(
            `${groupPath}.defaultChoiceId`,
            'must reference a choice in this group',
          );
        choices.forEach((rawChoice, choiceIndex) => {
          const choice = record(
            rawChoice,
            `${groupPath}.choices[${choiceIndex}]`,
          );
          string(choice.label, `${groupPath}.choices[${choiceIndex}].label`);
          if (choice.summaryLabel !== undefined) {
            string(
              choice.summaryLabel,
              `${groupPath}.choices[${choiceIndex}].summaryLabel`,
            );
          }
        });
      }
    });
    if (optionGroups.length > 0) {
      const configurationUi = record(
        platform.configurationUi,
        `${path}.configurationUi`,
      );
      string(
        configurationUi.changeLabel,
        `${path}.configurationUi.changeLabel`,
      );
      if (configurationUi.contextNote !== undefined) {
        string(
          configurationUi.contextNote,
          `${path}.configurationUi.contextNote`,
        );
      }
      if (
        configurationUi.selectedOptionIsFeeFormula !== undefined &&
        typeof configurationUi.selectedOptionIsFeeFormula !== 'boolean'
      ) {
        fail(
          `${path}.configurationUi.selectedOptionIsFeeFormula`,
          'expected a boolean',
        );
      }
      if (
        configurationUi.detailPresentation !== undefined &&
        !['configuration-cards', 'controls'].includes(
          configurationUi.detailPresentation,
        )
      ) {
        fail(
          `${path}.configurationUi.detailPresentation`,
          'unsupported detail presentation',
        );
      }
    }
    optionGroups.forEach((rawGroup, groupIndex) => {
      const groupPath = `${path}.optionGroups[${groupIndex}]`;
      const group = record(rawGroup, groupPath);
      validateConditions(
        group.visibleWhen,
        `${groupPath}.visibleWhen`,
        optionChoices,
      );
      (group.choices ?? []).forEach((choice, choiceIndex) => {
        if (choice.fundingBehavior !== undefined)
          validateFunding(
            choice.fundingBehavior,
            `${groupPath}.choices[${choiceIndex}].fundingBehavior`,
            feeGroups,
          );
        if (choice.regionLabel !== undefined) {
          string(
            choice.regionLabel,
            `${groupPath}.choices[${choiceIndex}].regionLabel`,
          );
        }
        if (choice.evidence !== undefined) {
          const choiceEvidencePath = `${groupPath}.choices[${choiceIndex}].evidence`;
          const choiceEvidence = record(choice.evidence, choiceEvidencePath);
          const choiceStatus = string(
            choiceEvidence.status,
            `${choiceEvidencePath}.status`,
          );
          if (!EVIDENCE_STATUSES.has(choiceStatus)) {
            fail(`${choiceEvidencePath}.status`, 'unsupported evidence status');
          }
          string(choiceEvidence.label, `${choiceEvidencePath}.label`);
          string(
            choiceEvidence.description,
            `${choiceEvidencePath}.description`,
          );
          array(
            choiceEvidence.sourceIds,
            `${choiceEvidencePath}.sourceIds`,
          ).forEach((sourceId, sourceIndex) => {
            if (!sourceIds.has(sourceId)) {
              fail(
                `${choiceEvidencePath}.sourceIds[${sourceIndex}]`,
                `unknown source "${sourceId}"`,
              );
            }
          });
        }
      });
    });
    groups.forEach((group, groupIndex) =>
      validateConditions(
        group.enabledWhen,
        `${path}.feeGroups[${groupIndex}].enabledWhen`,
        optionChoices,
      ),
    );
    groups.forEach((group, groupIndex) => {
      group.rules.forEach((rule, ruleIndex) => {
        if (rule.kind !== 'percentage-from-option') return;
        const optionGroup = optionGroups.find(
          (candidate) => candidate.id === rule.optionGroupId,
        );
        if (!optionGroup?.input || optionGroup.input.kind !== 'percentage') {
          fail(
            `${path}.feeGroups[${groupIndex}].rules[${ruleIndex}].optionGroupId`,
            'must reference a percentage input option group',
          );
        }
      });
    });
    if (platform.tieredScalePresentation !== undefined) {
      const presentationPath = `${path}.tieredScalePresentation`;
      const presentation = record(
        platform.tieredScalePresentation,
        presentationPath,
      );
      const presentationGroup = feeGroups.get(
        string(presentation.feeGroupId, `${presentationPath}.feeGroupId`),
      );
      if (
        !presentationGroup?.rules.some(
          (rule) => rule.kind === 'tiered-percentage-by-total',
        )
      ) {
        fail(
          `${presentationPath}.feeGroupId`,
          'must reference a tiered percentage fee group',
        );
      }
      [
        'eyebrow',
        'heading',
        'amountLabel',
        'organizationRetainedLabel',
        'feeShareLabel',
        'note',
      ].forEach((field) =>
        string(presentation[field], `${presentationPath}.${field}`),
      );
      if (
        presentation.amountDisplay !== undefined &&
        presentation.amountDisplay !== 'whole-dollar-ranges'
      ) {
        fail(`${presentationPath}.amountDisplay`, 'unsupported amount display');
      }
    }
    if (platform.defaultFundingBehavior !== undefined)
      validateFunding(
        platform.defaultFundingBehavior,
        `${path}.defaultFundingBehavior`,
        feeGroups,
      );

    array(platform.publishedFacts, `${path}.publishedFacts`).forEach(
      (rawFact, factIndex) => {
        const factPath = `${path}.publishedFacts[${factIndex}]`;
        const fact = record(rawFact, factPath);
        string(fact.id, `${factPath}.id`);
        string(fact.label, `${factPath}.label`);
        string(fact.value, `${factPath}.value`);
        string(fact.explanation, `${factPath}.explanation`);
        if (!EVIDENCE_STATUSES.has(fact.evidenceStatus))
          fail(`${factPath}.evidenceStatus`, 'unsupported evidence status');
        array(fact.sourceIds, `${factPath}.sourceIds`).forEach(
          (sourceId, sourceIndex) => {
            if (!sourceIds.has(sourceId))
              fail(
                `${factPath}.sourceIds[${sourceIndex}]`,
                `unknown source "${sourceId}"`,
              );
          },
        );
      },
    );
  });

  categories.forEach((category, categoryIndex) => {
    const hasEnabledPlatform = platforms.some(
      (platform) =>
        platform.comparisonEnabled === true &&
        platform.categoryId === category.id,
    );
    if (!hasEnabledPlatform) {
      fail(
        `$.platformCategories[${categoryIndex}]`,
        'must contain at least one enabled platform',
      );
    }
  });

  return value;
}
